/**
 * Billing API client + display pricing.
 *
 * Entry pricing (founder decision 2026-11-02): Owner $39/mo, Sales $39/mo, Solo
 * bundle (Owner + Sales) $59/mo. Flat, one user, monthly only. The first
 * subscription starts with a 14-day trial that needs a card and charges on day
 * 15. Mirrors apps/api ENTRY_PRICING and scripts/setup-stripe-catalog.mjs —
 * change all three together.
 */
import { api } from './api';

// ─── Types ──────────────────────────────────────────────────────────────

export type BillingInterval = 'month' | 'year';
export type SubscriptionStatus =
  | 'trialing' | 'active' | 'past_due'
  | 'canceled' | 'unpaid' | 'incomplete' | 'paused';

export interface SubscriptionInfo {
  plan: 'trial' | 'active';
  status: SubscriptionStatus;
  trialEndsAt: string | null;
  trialDaysRemaining: number | null;
  hasStripeCustomer: boolean;
  /** First subscription → the 14-day card trial; otherwise checkout charges today. */
  trialEligible: boolean;
  subscription: {
    id: string;
    seats: number;
    status: SubscriptionStatus;
    billingInterval: BillingInterval;
    amountCents: number;
    currency: string;
    currentPeriodStart: string;
    currentPeriodEnd: string;
    cancelAtPeriodEnd: boolean;
    canceledAt: string | null;
    trialEnd: string | null;
  } | null;
}

// ─── Plans ──────────────────────────────────────────────────────────────

/** What checkout buys. 'bundle' = the Solo bundle (Owner + Sales). */
export type CheckoutProduct = 'owner' | 'sales' | 'bundle';

export const TRIAL_DAYS = 14;

/** AI credits per month on every entry plan (apps/api PLANS.starter). */
export const PLAN_CREDITS = 300;

export interface PlanInfo {
  product: CheckoutProduct;
  name: string;
  monthlyCents: number;
  blurb: string;
  features: string[];
}

/** The three self-serve plans, in display order. Every claim must hold for a
 *  workspace that connected the sources it depends on (product-pages.ts rule). */
export const PLANS: PlanInfo[] = [
  {
    product: 'owner',
    name: 'Owner',
    monthlyCents: 3_900,
    blurb: 'The owner dashboard and daily briefing for your business',
    features: [
      'Daily AI briefing, never metered',
      'Client churn and expansion, AR recovery and payment-date predictions, each once your data supports it',
      'Action inbox with approval before anything is sent',
      'QuickBooks, Stripe, HubSpot, GoHighLevel, Gmail, Outlook, Google Calendar and more',
    ],
  },
  {
    product: 'sales',
    name: 'Sales',
    monthlyCents: 3_900,
    blurb: 'Pipeline, coaching and forecast for a seller',
    features: [
      'Deal risk and win likelihood once your CRM is connected',
      'AI deal coaching, talk tracks and battle cards',
      'Meeting prep and follow-up tracking',
      'Evidence forecast next to what your CRM says',
    ],
  },
  {
    product: 'bundle',
    name: 'Solo',
    monthlyCents: 5_900,
    blurb: 'Owner + Sales together, for the founder who runs the business and sells',
    features: [
      'Everything in Owner',
      'Everything in Sales',
      'One login, one bill — $19 less than buying both',
    ],
  },
];

export function planFor(product: CheckoutProduct): PlanInfo {
  return PLANS.find((p) => p.product === product)!;
}

/** What every plan shares — shown once under the cards. */
export const PLAN_COMMON = [
  '1 user',
  `${PLAN_CREDITS} AI credits / month (coaching, chat, drafts); predictions never metered`,
  'Billed monthly, cancel anytime',
];

/** The trial disclosure. Shown wherever someone can start a trial, and on
 *  Stripe Checkout itself (apps/api checkoutTermsText). Keep it plain: a
 *  card-required auto-charge that isn't stated clearly causes chargebacks. */
export function trialTerms(product?: CheckoutProduct): string {
  const price = product ? `${formatPrice(planFor(product).monthlyCents)}/mo` : 'your plan price';
  return `${TRIAL_DAYS}-day free trial. Card required. You're charged ${price} on day ${TRIAL_DAYS + 1} unless you cancel before then.`;
}

/** One-time AI credit top-up (mirrors apps/api POST /v1/billing/topup). */
export const TOPUP = { credits: 250, cents: 1_000 } as const;

/** The plan a workspace buys by default: a Sales workspace → Sales, else Owner. */
export function defaultPlanFor(workspaceKind: string | undefined | null): CheckoutProduct {
  return workspaceKind === 'sales' ? 'sales' : 'owner';
}

/** True for a standalone product workspace (Sales / Clients): one seat class,
 *  no owner dashboard. */
export function isStandaloneWorkspace(workspaceKind: string | undefined | null): boolean {
  return workspaceKind === 'sales' || workspaceKind === 'clients';
}

// ─── API calls ──────────────────────────────────────────────────────────

export function getSubscription(): Promise<SubscriptionInfo> {
  return api<SubscriptionInfo>('/v1/billing/subscription');
}

/** One seat class's usage (full = plan seats, team = Sales/Clients-pinned seats). */
export interface SeatBucket {
  used: number;
  members: number;
  pending: number;
  /** paid Stripe quantity for this class; null on a trial / no subscription */
  purchased: number | null;
  /** resolved effective cap for this class; null when unlimited */
  limit: number | null;
}

export interface SeatUsage {
  /** total (full + team): members + pending invites */
  used: number;
  members: number;
  pending: number;
  /** paid full-seat Stripe quantity; null on a trial / no subscription */
  purchased: number | null;
  /** the tier's structural ceiling on TOTAL seats; null when unlimited */
  planMax: number | null;
  /** resolved effective full-seat cap (purchased capped by planMax, or planMax on trial) */
  limit: number | null;
  /** full-seat bucket (owner/admin + full-access members) */
  full: SeatBucket;
  /** team-seat bucket (Sales/Clients-pinned employees) */
  team: SeatBucket;
}

export interface Entitlements {
  plan: string;
  planLabel: string;
  seats: SeatUsage;
  seatLimit: number | null;
  credits: { monthly: number; used: number; remaining: number };
}

/** Plan entitlements + this month's AI-credit usage (computed from agent_runs). */
export function getEntitlements(): Promise<Entitlements> {
  return api<Entitlements>('/v1/billing/entitlements');
}

/** Start Stripe Checkout for a plan. The API decides the trial (first
 *  subscription only); returns the Checkout URL to redirect to. */
export async function createCheckout(product: CheckoutProduct): Promise<string> {
  const res = await api<{ url: string; sessionId: string }>('/v1/billing/checkout', {
    method: 'POST',
    body: JSON.stringify({ product }),
  });
  return res.url;
}

export async function createPortal(): Promise<string> {
  const res = await api<{ url: string }>('/v1/billing/portal', {
    method: 'POST',
  });
  return res.url;
}

// ─── Cancel intercept ───────────────────────────────────────────────────

export type CancelReason = 'too_expensive' | 'not_using' | 'missing_feature' | 'switching' | 'other';
/** 'stayed' is recorded when someone opens the dialog and decides to stay —
 *  without that row the save rate is unknowable. */
export type CancelChoice = 'pause' | 'discount' | 'cancel' | 'stayed';

export interface CancelOffers {
  pause: boolean;
  /** False unless a save coupon is actually configured in Stripe — we never
   *  show an offer we cannot apply. */
  discount: boolean;
}

export function getCancelOffers(): Promise<CancelOffers> {
  return api<CancelOffers>('/v1/billing/cancel-offers');
}

/** Record the intent, and apply the offer for pause/discount. A Stripe failure
 *  comes back as { ok:false, error } rather than throwing — the reason is
 *  already stored either way. */
export function submitCancelIntent(input: {
  reason: CancelReason;
  note?: string;
  choice: CancelChoice;
}): Promise<{ ok: boolean; offerApplied: string | null; error?: string }> {
  return api('/v1/billing/cancel-intent', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** Start a one-time AI credit top-up checkout ($10 / 250 credits). Returns the
 *  Stripe Checkout URL to redirect to. */
export async function createTopupCheckout(): Promise<string> {
  const res = await api<{ url: string; sessionId: string }>('/v1/billing/topup', { method: 'POST' });
  return res.url;
}

// ─── Helpers ────────────────────────────────────────────────────────────

export function formatPrice(cents: number): string {
  return `$${Math.floor(cents / 100).toLocaleString()}`;
}
